import { env } from "@/lib/env";
import { supabase } from "@/integrations/supabase/client";
import {
  ApiError,
  AUTH_SESSION_EXPIRED_EVENT,
  apiDelete,
  apiGet,
} from "@/lib/apiClient";
import type {
  CompletedUpload,
  UploadCatalogItem,
  UploadContextKey,
} from "./types";
import {
  formatUploadHttpError,
  formatUploadNetworkError,
} from "./upload.errors";

type CatalogResponse = { items: UploadCatalogItem[] };

type SuccessEnvelope<TData> = {
  success: true;
  message: string;
  data: TData;
};

type ErrorEnvelope = {
  success: false;
  message: string;
  error: { code: string; details?: Record<string, unknown> };
};

async function authHeaders(): Promise<HeadersInit> {
  const appToken = localStorage.getItem("accessToken");
  if (appToken) {
    return { Authorization: `Bearer ${appToken}` };
  }

  const { data } = await supabase.auth.getSession();
  const supabaseToken = data.session?.access_token;
  return supabaseToken ? { Authorization: `Bearer ${supabaseToken}` } : {};
}

/** GET /api/uploads/catalog — folder mapping + limits from the API. */
export async function fetchUploadCatalog(): Promise<UploadCatalogItem[]> {
  const data = await apiGet<CatalogResponse>("/uploads/catalog");
  return data.items;
}

/**
 * DELETE /api/uploads — drop a file this user uploaded but never attached.
 *
 * Used when a half-finished record is discarded, so its images don't sit in the
 * bucket forever. The API refuses anything already attached to a record, and
 * treats an already-deleted file as success — so this is safe to call for every
 * file in a batch without tracking which ones actually made it.
 */
export async function deleteUploadedFile(objectKey: string): Promise<void> {
  await apiDelete<null>(`/uploads?objectKey=${encodeURIComponent(objectKey)}`);
}

export type UploadFileViaApiOptions = {
  /** Shown in 413 / size-limit copy when the proxy or API rejects the file. */
  maxBytesPerFile?: number;
};

/** POST /api/uploads — API uploads to S3; browser only sends the file + key. */
export async function uploadFileViaApi(
  key: UploadContextKey,
  file: File,
  options?: UploadFileViaApiOptions,
): Promise<CompletedUpload> {
  const formData = new FormData();
  formData.append("key", key);
  formData.append("file", file);

  const base = env.API_URL.replace(/\/$/, "");
  let response: Response;
  try {
    response = await fetch(`${base}/api/uploads`, {
      method: "POST",
      headers: await authHeaders(),
      body: formData,
    });
  } catch {
    throw new ApiError(0, formatUploadNetworkError(file), "UPLOAD_NETWORK_ERROR");
  }

  let body: SuccessEnvelope<CompletedUpload> | ErrorEnvelope | null = null;
  try {
    body = (await response.json()) as
      | SuccessEnvelope<CompletedUpload>
      | ErrorEnvelope;
  } catch {
    body = null;
  }

  if (!response.ok) {
    if (response.status === 401) {
      window.dispatchEvent(new Event(AUTH_SESSION_EXPIRED_EVENT));
    }
    if (body && body.success === false) {
      throw new ApiError(
        response.status,
        formatUploadHttpError(
          response.status,
          body.message,
          file,
          typeof body.error.details?.maxBytes === "number"
            ? body.error.details.maxBytes
            : options?.maxBytesPerFile,
          body.message,
        ),
        body.error.code,
        body.error.details,
      );
    }
    throw new ApiError(
      response.status,
      formatUploadHttpError(
        response.status,
        response.statusText,
        file,
        options?.maxBytesPerFile,
      ),
    );
  }

  if (!body || body.success !== true) {
    throw new ApiError(response.status, "Malformed response from the API");
  }

  return body.data;
}
